import { Fragment, useCallback, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useEidGrid } from 'components'

import {
    useCsvInbox,
    useGroupToBusinessRoleItems,
    useUpdateGroupToBusinessRoleItemStatus,
    useGroupToBusinessRoleUpdate,
} from 'hooks'
import { usePageContext } from 'pageContext'
import useNotification from 'packages/eid-ui/Notification/useNotification'

import {
    EditableGridTable,
    EditableGridRow,
    EditableGridTableHead,
} from 'components/EditableGrid'
import attributes from './attributes'
import ExpandedRow from './ExpandedRow'

const GroupToBusinessRoleGrid = ({
    trackingID,
    statusId,
    setTotalCount,
    subActiveTab,
    refreshTrigger,
    canEditInbox,
}) => {
    const { t } = useTranslation()
    const [state, dispatch] = usePageContext()
    const { showWarningMessage } = useNotification()
    const [updatingItemId, setUpdatingItemId] = useState(null)
    const [savingItemId, setSavingItemId] = useState(null)
    const [savedItemId, setSavedItemId] = useState(null)
    const [dataVersion, setDataVersion] = useState(0) // Increments on each refetch to force row re-render
    const wasFetchingRef = useRef(false)
    const { data: csvInboxData, isLoading: isCsvInboxLoading } =
        useCsvInbox(trackingID)
    const {
        list,
        totalCount,
        isInitialLoading: isLoading,
        loadingMore: isLoadingMore,
        loading: isFetching, // true during any fetch including refetch
        pagination,
        refetch,
        rest,
    } = useGroupToBusinessRoleItems(trackingID, statusId)

    // // Increment dataVersion when refetch completes (isFetching goes from true to false)
    // useEffect(() => {
    //     if (wasFetchingRef.current && !isFetching) {
    //         setDataVersion((v) => v + 1)
    //     }
    //     wasFetchingRef.current = isFetching
    // }, [isFetching])

    const { mutate: updateStatus } = useUpdateGroupToBusinessRoleItemStatus()
    const { mutate: updateRow, isLoading: isUpdating } =
        useGroupToBusinessRoleUpdate()

    // Handle save from EditableGridRow (on blur for text, immediately for dropdowns)
    const handleSave = useCallback(
        (updatedItem, originalItem, onSuccess) => {
            const itemId = updatedItem.rtA_GroupToBusinessRoleID
            setSavingItemId(itemId)

            // updatedItem already contains only the modified field + rtA_GroupToBusinessRoleID
            updateRow(updatedItem, {
                onSuccess: (response) => {
                    setSavingItemId(null)
                    if (response?.data?.isSuccess === false) {
                        showWarningMessage(
                            response?.data?.message || t('ErrorSaving'),
                        )
                    } else {
                        // Show "Saved" label - will be cleared when user starts modifying again
                        setSavedItemId(itemId)
                        refetch()
                        // Call success callback
                        if (onSuccess) {
                            onSuccess()
                        }
                    }
                },
                onError: (error) => {
                    // setSavedItemId(itemId)

                    // // Call success callback
                    // if (onSuccess) {
                    //     onSuccess()
                    // }

                    setSavingItemId(null)
                    console.error('Error saving row:', error)
                    showWarningMessage(error?.message || t('ErrorSaving'))
                },
            })
        },
        [updateRow, showWarningMessage],
    )

    useEffect(() => {
        if (subActiveTab === 'all') {
            setTotalCount(totalCount)
        }
    }, [totalCount])

    // Watch for refresh trigger and refetch data
    useEffect(() => {
        if (refreshTrigger !== undefined) {
            refetch()
        }
    }, [refreshTrigger, refetch])

    // Handle status change from InboxStatusIndicator
    const handleStatusChange = useCallback(
        (payload, item, newStatus, onSuccess) => {
            // Set the item being updated to show loader
            setUpdatingItemId(item.rtA_GroupToBusinessRoleID)

            updateStatus(payload, {
                onSuccess: (response) => {
                    setUpdatingItemId(null)
                    // Check if the API response indicates success
                    if (response?.data?.isSuccess === false) {
                        // Show error message - status stays as original
                        showWarningMessage(
                            response?.data?.message || t('ErrorUpdatingStatus'),
                        )
                    } else {
                        // Success - call onSuccess to update the status visually
                        if (onSuccess) {
                            onSuccess(newStatus)
                        }
                        // Optionally refetch data after successful update
                        refetch()
                    }
                },
                onError: (error) => {
                    setUpdatingItemId(null)
                    showWarningMessage(
                        error?.message || t('ErrorUpdatingStatus'),
                    )
                    // Status stays as original - no change needed
                },
            })
        },
        [updateStatus, showWarningMessage, t],
    )

    const { viewSwitcher, setCurrentView, viewToRender } = useEidGrid({
        // isLoading: isLoading || state?.landscapes === '',
        isLoading: isFetching || isUpdating || isCsvInboxLoading,
        data: list || [],
        isLoadingMore,
        noDataMessage: t('NoDataMessage'),
        pagination,
        attributes: attributes,

        tableViewConfig: {
            TableComponent: EditableGridTable,
            TableHeadComponent: EditableGridTableHead,
            TableRowComponent: EditableGridRow,
            wrapTableInScrollContainer: true,
            expandableComponent: (data) => <ExpandedRow data={data} />,
            isRowClickable: false,
            rowProps: {
                onStatusChange: handleStatusChange,
                updatingItemId,
                isRowEditable: (item) =>
                    [1, 2, 3].includes(item?.rtA_CSVInboxItemStatusID) &&
                    !csvInboxData?.isClosed &&
                    canEditInbox,
                onSave: handleSave,
                savingItemId,
                savedItemId,
                itemIdProp: 'rtA_GroupToBusinessRoleID',
                isFetching,
                // dataVersion, // Increments on refetch - used in row key to force re-render
            },
        },
    })

    return viewToRender
}

export default GroupToBusinessRoleGrid
